import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Bot, Check, Loader2, Sparkles, Users, X } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useAuth } from "@/context/AuthContext";

export default function SubmitServer() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  // preview is set after bot install completes (via BotCallback stashing to sessionStorage)
  const [preview, setPreview] = useState(null);
  const [categoriesList, setCategoriesList] = useState([]);
  const [selected, setSelected] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState("");
  const [description, setDescription] = useState("");
  const [longDescription, setLongDescription] = useState("");
  const [installing, setInstalling] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/");
  }, [loading, user, navigate]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/categories");
      setCategoriesList(data);
    })();
  }, []);

  // On mount, pick up any preview that the callback page stashed
  useEffect(() => {
    const raw = sessionStorage.getItem("bot_preview");
    if (raw) {
      try {
        const p = JSON.parse(raw);
        setPreview(p);
        setDescription(p.description ? p.description.slice(0, 200) : "");
        sessionStorage.removeItem("bot_preview");
        if (p.duplicate) {
          toast.error(
            p.duplicate.owned_by_me
              ? "You already listed this server."
              : "This server is already listed by someone else."
          );
          if (p.duplicate.slug) {
            navigate(`/servers/${p.duplicate.slug}`, { replace: true });
          }
        }
      } catch {
        // ignore
      }
    }
  }, [navigate]);

  const addBotToServer = async () => {
    setInstalling(true);
    try {
      const { data } = await api.get("/servers/bot/install-url", {
        params: { origin: window.location.origin },
      });
      // Break out of any iframe (Emergent preview)
      try {
        window.top.location.href = data.url;
      } catch {
        window.location.href = data.url;
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start bot install");
      setInstalling(false);
    }
  };

  const toggleCat = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : prev.length >= 3 ? prev : [...prev, id]
    );
  };

  const addTag = () => {
    const t = tagInput.trim().toLowerCase().replace(/[^a-z0-9-]/g, "");
    if (!t || tags.includes(t) || tags.length >= 8) return;
    setTags([...tags, t]);
    setTagInput("");
  };

  const submit = async () => {
    if (!description.trim()) return toast.error("Add a short description");
    if (selected.length === 0) return toast.error("Pick at least one category");
    if (!preview?.publish_state) return toast.error("Ownership token missing — add the bot again");

    setSubmitting(true);
    try {
      const { data } = await api.post("/servers", {
        guild_id: preview.guild_id,
        state: preview.publish_state,
        description: description.trim(),
        long_description: longDescription.trim(),
        categories: selected,
        tags,
      });
      toast.success("Your server is listed!");
      navigate(`/servers/${data.slug || data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submit failed");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="submit-page">
      <SEO
        title="Add your Discord server"
        description="List your Discord server on Serverlistr. Add our bot to verify ownership and get discovered instantly."
        path="/submit"
        noindex
      />
      <Breadcrumbs items={[{ name: "Add your server" }]} />
      <div className="text-xs font-bold uppercase tracking-[0.2em] text-neon-pink mb-2">List a server</div>
      <h1 className="font-heading text-3xl sm:text-4xl font-black">Add your Discord server</h1>
      <p className="mt-2 text-sm text-zinc-400">
        Add the Serverlistr bot to your server — that&apos;s how we verify you actually run it.
        Takes 15 seconds. No permissions except the ability to create invites.
      </p>

      <div className="mt-8 flex items-center gap-2 text-xs font-mono">
        <StepDot n={1} active label="Add bot" done={!!preview} />
        <div className="h-px w-8 bg-white/10" />
        <StepDot n={2} active={!!preview} label="Details" />
      </div>

      {!preview && (
        <div className="mt-8 rounded-2xl bg-[#0A0A0F] border border-white/5 p-6 sm:p-8" data-testid="step-add-bot">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-blurple/15 border border-blurple/40 flex items-center justify-center shrink-0">
              <Bot className="w-7 h-7 text-blurple" />
            </div>
            <div>
              <div className="font-heading text-lg font-bold">Add Serverlistr Bot</div>
              <div className="text-xs text-zinc-500 mt-1">
                You&apos;ll be sent to Discord, pick your server, click Authorize. Done.
              </div>
            </div>
          </div>

          <ul className="mt-6 space-y-2 text-sm text-zinc-400">
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-neon-green mt-0.5 shrink-0" /> Verifies you have <span className="text-white font-semibold">Manage Server</span> permission</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-neon-green mt-0.5 shrink-0" /> Auto-fetches your name, icon, banner, member count</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-neon-green mt-0.5 shrink-0" /> Only needs <span className="font-mono text-white">Create Invite</span> permission — nothing else</li>
            <li className="flex items-start gap-2"><Check className="w-4 h-4 text-neon-green mt-0.5 shrink-0" /> Never messages your members, never reads chat</li>
          </ul>

          <button
            data-testid="add-bot-btn"
            onClick={addBotToServer}
            disabled={installing}
            className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-6 rounded-xl bg-blurple hover:bg-blurple-hover text-white font-bold transition-all hover:shadow-[0_0_30px_rgba(88,101,242,0.5)] disabled:opacity-50 active:scale-95"
          >
            {installing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
            Add Serverlistr Bot
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {preview && (
        <div className="mt-8 space-y-6" data-testid="step-details">
          <div className="rounded-2xl bg-[#0A0A0F] border border-blurple/40 p-6" data-testid="preview-card">
            <div className="flex items-start gap-4">
              <img
                src={preview.icon_url || "https://cdn.discordapp.com/embed/avatars/0.png"}
                alt=""
                className="w-16 h-16 rounded-2xl object-cover bg-[#12121A]"
              />
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-neon-green mb-1">
                  <Check className="w-3 h-3" /> Verified ownership
                </div>
                <div className="font-heading text-xl font-bold truncate">{preview.name}</div>
                <div className="text-xs font-mono text-zinc-400 mt-1 flex items-center flex-wrap gap-3">
                  <span className="inline-flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-neon-green" />{preview.online_count?.toLocaleString?.()} online</span>
                  <span className="inline-flex items-center gap-1"><Users className="w-3 h-3" />{preview.member_count?.toLocaleString?.()} members</span>
                  {preview.invite_url && (
                    <a href={preview.invite_url} target="_blank" rel="noreferrer" className="text-blurple hover:text-blurple-hover">{preview.invite_url}</a>
                  )}
                </div>
              </div>
              <button
                onClick={() => { setPreview(null); setDescription(""); }}
                className="text-xs text-zinc-500 hover:text-white"
              >
                Change server
              </button>
            </div>
          </div>

          <div className="rounded-2xl bg-[#0A0A0F] border border-white/5 p-6">
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Short description</label>
            <input
              data-testid="description-input"
              maxLength={200}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="A one-liner that hooks people..."
              className="mt-2 w-full h-11 px-3 bg-[#12121A] border border-white/10 rounded-lg placeholder-zinc-500 focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple"
            />
            <div className="text-right text-xs text-zinc-500 mt-1 font-mono">{description.length}/200</div>

            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mt-4 block">Long description (optional)</label>
            <Textarea
              data-testid="long-description-input"
              rows={5}
              maxLength={2000}
              value={longDescription}
              onChange={(e) => setLongDescription(e.target.value)}
              placeholder="Tell people about your community..."
              className="mt-2 bg-[#12121A] border-white/10 focus:border-blurple text-white"
            />
          </div>

          <div className="rounded-2xl bg-[#0A0A0F] border border-white/5 p-6">
            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400">Categories · pick up to 3</label>
            <div className="mt-3 flex flex-wrap gap-2">
              {categoriesList.map((c) => {
                const on = selected.includes(c.id);
                return (
                  <button
                    key={c.id}
                    data-testid={`cat-toggle-${c.id}`}
                    onClick={() => toggleCat(c.id)}
                    className={`inline-flex items-center gap-1 h-8 px-3 rounded-full text-xs font-semibold border transition-all ${
                      on
                        ? "bg-blurple/15 border-blurple text-white"
                        : "bg-white/5 border-white/10 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {on && <Check className="w-3 h-3" />} {c.name}
                  </button>
                );
              })}
            </div>

            <label className="text-xs font-bold uppercase tracking-[0.2em] text-zinc-400 mt-6 block">Tags · up to 8</label>
            <div className="mt-2 flex gap-2">
              <input
                data-testid="tag-input"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(); } }}
                placeholder="e.g. minecraft, chill, lgbtq"
                className="flex-1 h-10 px-3 bg-[#12121A] border border-white/10 rounded-lg text-sm placeholder-zinc-500 focus:border-blurple focus:outline-none focus:ring-1 focus:ring-blurple"
              />
              <button data-testid="add-tag-btn" onClick={addTag} className="h-10 px-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold">Add</button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {tags.map((t) => (
                <Badge key={t} className="bg-white/5 text-zinc-300 border border-white/10 gap-1">
                  #{t}
                  <button onClick={() => setTags(tags.filter((x) => x !== t))}><X className="w-3 h-3" /></button>
                </Badge>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button
              onClick={() => { setPreview(null); setDescription(""); }}
              className="h-11 px-5 rounded-lg bg-white/5 hover:bg-white/10 border border-white/10 text-sm font-semibold"
            >
              Back
            </button>
            <button
              data-testid="submit-server-btn"
              onClick={submit}
              disabled={submitting}
              className="h-11 px-6 rounded-lg bg-blurple hover:bg-blurple-hover text-white font-bold transition-all hover:shadow-[0_0_20px_rgba(88,101,242,0.4)] disabled:opacity-50"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4" /> Publish server</span>
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepDot({ n, active, label, done }) {
  return (
    <div className="flex items-center gap-2">
      <div className={`w-6 h-6 rounded-full flex items-center justify-center font-bold text-[10px] ${
        done ? "bg-neon-green text-black" : active ? "bg-blurple text-white" : "bg-white/5 text-zinc-500 border border-white/10"
      }`}>
        {done ? <Check className="w-3 h-3" /> : n}
      </div>
      <span className={`uppercase tracking-widest text-[10px] ${active ? "text-white" : "text-zinc-500"}`}>{label}</span>
    </div>
  );
}
